import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toPng } from "html-to-image";
import { Download, ShareNetwork, Flag, Trophy, ArrowRight, WhatsappLogo, XLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShareRound() {
  const { kind, id } = useParams(); // kind: 'round' | 'group'
  const [data, setData] = useState(null);
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const url = kind === "group" ? `/share/group-round/${id}` : `/share/round/${id}`;
    api.get(url).then((r) => setData(r.data)).catch(() => setData(false));
  }, [kind, id]);

  if (data === false) return <Wrapper><Empty /></Wrapper>;
  if (!data) return <Wrapper><div className="py-10 text-white/70">Loading…</div></Wrapper>;

  const isGroup = kind === "group";
  const playedAt = new Date(isGroup ? data.created_at : data.played_at).toLocaleDateString(undefined,
    { day: "numeric", month: "short", year: "numeric" });

  const downloadImage = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#0F2537" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `bulagolf-${isGroup ? "match" : "round"}-${id.slice(0, 8)}.png`;
      a.click();
      toast.success("Image saved");
    } catch {
      toast.error("Could not generate image");
    } finally { setBusy(false); }
  };

  const shareUrl = window.location.href;
  const shareText = isGroup
    ? `${data.match?.label || "Match"} at ${data.course_name} · Bula Golf`
    : `Shot ${data.total_strokes} (${data.total_strokes - data.total_par >= 0 ? "+" : ""}${data.total_strokes - data.total_par}) at ${data.course_name} · ${data.total_stableford} Stableford pts · Bula Golf`;

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Bula Golf", text: shareText, url: shareUrl }); } catch {}
    } else {
      navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("Link copied");
    }
  };

  return (
    <Wrapper>
      <div className="max-w-2xl mx-auto px-4 py-8 md:py-12">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2 text-white" data-testid="share-logo">
            <div className="w-9 h-9 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Flag weight="fill" className="text-[#E05D3A]" size={20} />
            </div>
            <div className="leading-none">
              <div className="font-heading font-black text-lg">Bula Golf</div>
              <div className="label-eyebrow !text-[0.55rem] !text-white/70">Fiji Handicap Tracker</div>
            </div>
          </Link>
          <Link to="/register" data-testid="share-cta-register" className="text-xs font-bold text-white/80 hover:text-white inline-flex items-center gap-1">
            Make your own <ArrowRight size={12} weight="bold" />
          </Link>
        </div>

        <div
          ref={cardRef}
          data-testid="share-card"
          className="relative overflow-hidden rounded-3xl shadow-2xl"
          style={{ background: "#0F2537" }}
        >
          {data.course_image && (
            <div className="absolute inset-0">
              <img src={data.course_image} alt="" crossOrigin="anonymous" className="w-full h-full object-cover opacity-30" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F2537]/85 via-[#0F2537]/60 to-[#0F2537]/95" />
            </div>
          )}

          <div className="relative p-7 md:p-10 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow !text-white/60">{isGroup ? data.format.toUpperCase() + " PLAY" : "ROUND CARD"}</div>
                <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-1 max-w-md">{data.course_name}</div>
                <div className="text-xs text-white/70 mt-1">{data.course_location} · {playedAt}</div>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
                {isGroup ? <Trophy weight="fill" className="text-[#E05D3A]" size={24} />
                         : <Flag weight="fill" className="text-[#E05D3A]" size={22} />}
              </div>
            </div>

            {!isGroup && <SoloHero data={data} />}
            {isGroup && <GroupHero data={data} />}

            <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
              <div className="text-xs text-white/60 font-semibold tracking-widest uppercase">
                Played on <span className="text-white font-bold">Bula Golf</span>
              </div>
              <div className="text-xs text-white/40">bulagolf.fj</div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <ShareBtn onClick={downloadImage} disabled={busy} testid="share-download"><Download size={16} weight="bold" /> {busy ? "Saving…" : "Save image"}</ShareBtn>
          <ShareBtn onClick={nativeShare} testid="share-native"><ShareNetwork size={16} weight="bold" /> Share</ShareBtn>
          <a href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`} target="_blank" rel="noreferrer"
            data-testid="share-whatsapp" className="inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-[#25D366] text-white text-sm font-bold hover:brightness-110">
            <WhatsappLogo size={16} weight="fill" /> WhatsApp
          </a>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer"
            data-testid="share-x" className="inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-black text-white text-sm font-bold hover:brightness-110">
            <XLogo size={16} weight="fill" /> Post
          </a>
        </div>

        <div className="mt-8 text-center">
          <Link to="/register" data-testid="share-bottom-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E05D3A] text-white font-bold hover:brightness-110">
            Track your own rounds → it's free <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </Wrapper>
  );
}

function SoloHero({ data }) {
  const vsPar = data.total_strokes - data.total_par;
  return (
    <>
      <div className="mt-7 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow !text-white/60">{data.user_name}</div>
          <div className="flex items-baseline gap-3 mt-1">
            <div className="font-heading font-black text-[7rem] md:text-[8rem] leading-none tracking-tighter" data-testid="share-total">
              {data.total_strokes}
            </div>
            <div>
              <div className={`font-heading font-black text-3xl ${vsPar < 0 ? "text-[#E05D3A]" : "text-white/70"}`}>
                {vsPar >= 0 ? "+" : ""}{vsPar}
              </div>
              <div className="label-eyebrow !text-white/50 mt-1">vs par</div>
            </div>
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-2xl p-4 min-w-[160px]">
          <div className="label-eyebrow !text-white/60">Stableford</div>
          <div className="font-heading font-black text-3xl mt-1">{data.total_stableford ?? "—"}<span className="text-base font-bold text-white/60"> pts</span></div>
          <div className="text-xs text-white/60 mt-2">{data.tees} tees · Diff {data.differential > 0 ? "+" : ""}{data.differential}</div>
        </div>
      </div>

      {data.best_hole && (
        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#E05D3A]/15 rounded-full">
          <span className="text-[#E05D3A] font-bold">★</span>
          <span className="text-sm font-bold">
            {data.best_hole.d === -1 ? "Birdie" : data.best_hole.d === -2 ? "Eagle" : data.best_hole.d <= -3 ? "Albatross" : "Best"} on hole {data.best_hole.hole}
          </span>
          <span className="text-xs text-white/70">({data.best_hole.score} on par {data.best_hole.par})</span>
        </div>
      )}
    </>
  );
}

function GroupHero({ data }) {
  const isMatch = data.format === "match";
  return (
    <>
      <div className="mt-6">
        {isMatch && data.match && (
          <div className="mb-5">
            <div className="label-eyebrow !text-white/60">Result</div>
            <div className="font-heading font-black text-5xl md:text-6xl mt-1 tracking-tighter text-[#E05D3A]" data-testid="share-match-label">
              {data.match.label}
            </div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {data.players.map((p, i) => {
            const winner = isMatch && data.match?.up !== 0
              ? ((i === 0 && data.match.up > 0) || (i === 1 && data.match.up < 0))
              : false;
            return (
              <div key={p.user_id} className={`p-4 rounded-2xl ${winner ? "bg-[#E05D3A]/20 border border-[#E05D3A]/40" : "bg-white/8 border border-white/10"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold">{p.user_name}{winner ? " 🏆" : ""}</div>
                    <div className="text-xs text-white/60">{p.tee_name} · CH {p.course_handicap ?? "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-black text-2xl">{p.total_strokes || "—"}</div>
                    <div className="text-xs text-white/60">{p.total_stableford ?? 0} pts</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ShareBtn({ children, onClick, disabled, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className="inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-[#0F2537] text-white text-sm font-bold hover:bg-[#0F2537]/90 disabled:opacity-60">
      {children}
    </button>
  );
}

function Empty() {
  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      <Flag size={32} weight="duotone" className="text-[#E05D3A] mx-auto" />
      <h1 className="font-heading font-black text-2xl text-white mt-4">Round not found</h1>
      <p className="text-white/60 mt-2">This share link may have been removed or never existed.</p>
      <Link to="/" className="mt-6 inline-block px-4 py-2 rounded-xl bg-[#E05D3A] text-white font-bold">Go home</Link>
    </div>
  );
}

function Wrapper({ children }) {
  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #0a1622 0%, #0F2537 50%, #1a3a52 100%)" }}>
      {children}
    </div>
  );
}
